import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Sparkle, WarningOctagon } from "@phosphor-icons/react";
import { toast } from "sonner";

const REASON_LABELS = {
    bounced: "Email bounced",
    wrong_department: "Wrong department / bot",
    no_response: "No response after weeks",
    closed_account: "Company closed / account moved",
    other: "Other",
};

/**
 * Combobox for the Creditor / Plaintiff field. Debounced-lookup against
 * `/api/creditors/lookup` — when the user picks a suggestion we set the name
 * AND emit the suggested contact email upward via `onEmailSuggest`.
 */
export default function CreditorNameAutocomplete({
    value,
    onChange,
    onEmailSuggest,
    required = false,
    inputId = "creditor",
    testId = "case-creditor",
    className = "mt-1.5 h-11",
    placeholder = "Start typing e.g. Portfolio Recovery",
}) {
    const [matches, setMatches] = useState([]);
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const [reportOpen, setReportOpen] = useState(false);
    const [reportTarget, setReportTarget] = useState(null);
    const [reportReason, setReportReason] = useState("bounced");
    const [reportNote, setReportNote] = useState("");
    const [reportSuggested, setReportSuggested] = useState("");
    const [reporting, setReporting] = useState(false);
    const skipNextLookupRef = useRef(false);
    const timerRef = useRef(null);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (skipNextLookupRef.current) {
            skipNextLookupRef.current = false;
            return;
        }
        if (!value || value.trim().length < 2) {
            setMatches([]);
            setOpen(false);
            return;
        }
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(async () => {
            try {
                const { data } = await api.get(`/creditors/lookup?q=${encodeURIComponent(value)}`);
                setMatches(data.matches || []);
                setOpen((data.matches || []).length > 0);
                setActive(0);
            } catch {
                setMatches([]);
                setOpen(false);
            }
        }, 220);
        return () => clearTimeout(timerRef.current);
    }, [value]);

    useEffect(() => {
        const onDocClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const pick = (m) => {
        skipNextLookupRef.current = true;
        onChange(m.name);
        onEmailSuggest?.(m.email, m);
        setOpen(false);
    };

    const openReport = (m) => {
        setReportTarget(m);
        setReportReason("bounced");
        setReportNote("");
        setReportSuggested("");
        setReportOpen(true);
        setOpen(false);
    };

    const submitReport = async () => {
        if (!reportTarget) return;
        setReporting(true);
        try {
            await api.post("/creditors/feedback", {
                name: reportTarget.name,
                email: reportTarget.email,
                reason: reportReason,
                note: reportNote || null,
                suggested_email: reportSuggested || null,
            });
            toast.success("Thanks — report logged. We'll audit this entry.");
            setReportOpen(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setReporting(false);
        }
    };

    const onKeyDown = (e) => {
        if (!open || matches.length === 0) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((i) => (i + 1) % matches.length);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => (i - 1 + matches.length) % matches.length);
        } else if (e.key === "Enter") {
            e.preventDefault();
            pick(matches[active]);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    return (
        <div className="relative" ref={wrapRef}>
            <Input
                data-testid={testId}
                id={inputId}
                required={required}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onFocus={() => matches.length > 0 && setOpen(true)}
                onKeyDown={onKeyDown}
                autoComplete="off"
                placeholder={placeholder}
                className={className}
            />
            {open && matches.length > 0 && (
                <ul
                    className="absolute z-40 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden"
                    data-testid="creditor-suggestions"
                    role="listbox"
                >
                    {matches.map((m, i) => (
                        <li
                            key={m.name}
                            role="option"
                            aria-selected={i === active}
                            onMouseEnter={() => setActive(i)}
                            className={`flex items-start gap-3 px-3 py-2.5 text-sm ${i === active ? "bg-slate-100" : "hover:bg-slate-50"}`}
                            data-testid={`creditor-suggestion-${i}`}
                        >
                            <button
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); pick(m); }}
                                className="flex items-start gap-3 flex-1 min-w-0 text-left cursor-pointer"
                                aria-label={`Pick ${m.name}`}
                            >
                                <Sparkle size={14} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-slate-900 truncate flex items-center gap-2">
                                        {m.name}
                                        {m.reported_count > 0 && (
                                            <span
                                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono bg-rose-100 text-rose-700 border border-rose-200"
                                                title={`${m.reported_count} user report${m.reported_count === 1 ? "" : "s"}`}
                                                data-testid={`creditor-reported-${i}`}
                                            >
                                                <WarningOctagon size={9} weight="fill" /> {m.reported_count}
                                            </span>
                                        )}
                                    </p>
                                    <p className="text-xs text-slate-500 font-mono truncate">{m.email}</p>
                                </div>
                                <span className="text-[10px] uppercase tracking-wider text-slate-400 shrink-0 mt-1">
                                    {m.kind.replace(/_/g, " ")}
                                </span>
                            </button>
                            <button
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); openReport(m); }}
                                className="shrink-0 h-6 w-6 rounded flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                                title="Report wrong / bounced email"
                                aria-label={`Report ${m.name}`}
                                data-testid={`creditor-report-${i}`}
                            >
                                <WarningOctagon size={13} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            <Dialog open={reportOpen} onOpenChange={setReportOpen}>
                <DialogContent className="max-w-md" data-testid="creditor-report-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-xl">Report wrong dispute email</DialogTitle>
                    </DialogHeader>
                    <div className="pt-2 space-y-4">
                        <div className="p-3 rounded-md bg-slate-50 border border-slate-200 text-sm">
                            <p className="font-medium text-slate-900">{reportTarget?.name}</p>
                            <p className="text-xs text-slate-600 font-mono mt-0.5">{reportTarget?.email}</p>
                        </div>
                        <div>
                            <Label htmlFor="report-reason" className="text-xs uppercase tracking-wider text-slate-500">What happened?</Label>
                            <Select value={reportReason} onValueChange={setReportReason}>
                                <SelectTrigger id="report-reason" className="mt-2" data-testid="report-reason-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(REASON_LABELS).map(([v, label]) => (
                                        <SelectItem key={v} value={v}>{label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label htmlFor="report-suggested" className="text-xs uppercase tracking-wider text-slate-500">
                                Do you know a better email? <span className="text-slate-400 normal-case">(optional)</span>
                            </Label>
                            <Input
                                id="report-suggested"
                                type="email"
                                value={reportSuggested}
                                onChange={(e) => setReportSuggested(e.target.value)}
                                placeholder="disputes@newdomain.com"
                                className="mt-2"
                                data-testid="report-suggested-email"
                            />
                        </div>
                        <div>
                            <Label htmlFor="report-note" className="text-xs uppercase tracking-wider text-slate-500">
                                Note <span className="text-slate-400 normal-case">(optional, 500 chars)</span>
                            </Label>
                            <Textarea
                                id="report-note"
                                rows={3}
                                value={reportNote}
                                onChange={(e) => setReportNote(e.target.value.slice(0, 500))}
                                placeholder="e.g. Received a bounce mailer-daemon reply on 07/13."
                                className="mt-2"
                                data-testid="report-note"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReportOpen(false)} data-testid="report-cancel">Cancel</Button>
                        <Button
                            onClick={submitReport}
                            disabled={reporting}
                            className="bg-[#0F1E3D] hover:bg-[#1A2C57]"
                            data-testid="report-submit"
                        >
                            {reporting ? "Sending…" : "Submit report"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
